/** Optional storefront motion on every page. Content is visible before, during and without this controller:
 * groups start at 35 % opacity at most, never hidden. Sections below the first screen reveal automatically,
 * grids marked with data-pg-motion-stagger come in one card after another, reduced motion gets a calm fade. */
(function () {
  'use strict';

  if (window.PGStorefrontMotion) {
    window.PGStorefrontMotion.init(document);
    return;
  }

  var selector = '[data-pg-motion-reveal]';
  // whole sections below the first screen, unless they bring their own groups or opt out
  var autoSelector = '#MainContent > .shopify-section:not(:first-child)';
  // product grids: every card on its own, cards arriving together follow 50 ms apart
  var itemSelector = '[data-pg-motion-items] > *';
  var seen = new WeakSet();
  var pending = new Set();
  var active = new Map();
  var keyboardUsed = false;
  var navigationKeys = ['Tab', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Spacebar'];
  var preference = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var observer = null;

  function cancel(element) {
    var animation = active.get(element);
    if (!animation) return;
    active.delete(element);
    animation.onfinish = null;
    animation.oncancel = null;
    try { animation.cancel(); } catch (error) { /* Base styles already show the content. */ }
  }

  function reveal(element) {
    pending.delete(element);
    if (observer) observer.unobserve(element);
    cancel(element);
  }

  function cancelActive() {
    Array.from(active.keys()).forEach(cancel);
  }

  function intersect(entries) {
    var batch = 0;
    entries.forEach(function (entry) {
      var element = entry.target;
      if (!element.isConnected) {
        reveal(element);
        return;
      }
      if (!entry.isIntersecting || !pending.has(element)) return;
      reveal(element);
      if (keyboardUsed || document.hidden || element.contains(document.activeElement)) return;
      var calm = Boolean(preference && preference.matches);
      var items = staggerItems(element);
      if (items.length) {
        items.forEach(function (item, index) { play(item, calm, Math.min(index, 7) * 50); });
      } else {
        play(element, calm, isGridItem(element) ? Math.min(batch++, 7) * 50 : 0);
      }
    });
  }

  // Reduced motion: a calm fade without movement. Otherwise a short rise with the strong ease-out.
  function play(element, calm, delay) {
    try {
      var animation = element.animate(calm ? [
        { opacity: 0.35 },
        { opacity: 1 }
      ] : [
        { opacity: 0.35, transform: 'translateY(8px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], {
        duration: calm ? 320 : 280,
        easing: calm ? 'ease' : 'cubic-bezier(.23,1,.32,1)',
        delay: delay,
        fill: delay ? 'backwards' : 'none'
      });
      if (!animation || typeof animation.cancel !== 'function') return;
      active.set(element, animation);
      function finished() {
        if (active.get(element) === animation) active.delete(element);
        animation.onfinish = null;
        animation.oncancel = null;
      }
      animation.onfinish = finished;
      animation.oncancel = finished;
    } catch (error) { /* Animation support must never affect access to content. */ }
  }

  function isGridItem(element) {
    var parent = element.parentElement;
    return Boolean(parent && typeof parent.hasAttribute === 'function' && parent.hasAttribute('data-pg-motion-items'));
  }

  function staggerItems(element) {
    if (typeof element.hasAttribute !== 'function') return [];
    if (element.hasAttribute('data-pg-motion-stagger')) return Array.prototype.slice.call(element.children || []);
    return Array.prototype.slice.call(element.querySelectorAll('[data-pg-motion-stagger] > *'));
  }

  if (typeof window.IntersectionObserver === 'function') {
    try { observer = new window.IntersectionObserver(intersect, { threshold: 0 }); } catch (error) {}
  }

  function initElement(element) {
    if (seen.has(element)) return;
    seen.add(element);
    if (!observer || typeof element.animate !== 'function' || keyboardUsed || document.hidden ||
        element.contains(document.activeElement)) return;
    // Preserve the initial view, including the browser's restored scroll position.
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (element.getBoundingClientRect().top < viewportHeight) return;
    pending.add(element);
    try { observer.observe(element); } catch (error) { reveal(element); }
  }

  function init(scope) {
    if (scope.matches && scope.matches(selector)) initElement(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(selector).forEach(initElement);
    if (scope.querySelectorAll) scope.querySelectorAll(itemSelector).forEach(initElement);
    if (scope.querySelectorAll) scope.querySelectorAll(autoSelector).forEach(function (section) {
      if (section.querySelector(selector) || section.querySelector('[data-pg-motion-off]')) return;
      initElement(section);
    });
  }

  function unload(scope) {
    Array.from(pending).forEach(function (element) {
      if (element === scope || scope.contains(element)) reveal(element);
    });
    Array.from(active.keys()).forEach(function (element) {
      if (element === scope || scope.contains(element)) cancel(element);
    });
  }

  window.PGStorefrontMotion = { init: init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener('keydown', function (event) {
    if (navigationKeys.indexOf(event.key) === -1) return;
    keyboardUsed = true;
    Array.from(pending).forEach(reveal);
    cancelActive();
  }, true);
  document.addEventListener('focusin', function (event) {
    Array.from(pending).forEach(function (element) {
      if (element.contains(event.target)) reveal(element);
    });
    Array.from(active.keys()).forEach(function (element) {
      if (element.contains(event.target)) cancel(element);
    });
  });
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) cancelActive();
  });
  if (preference) {
    if (preference.addEventListener) preference.addEventListener('change', cancelActive);
    else if (preference.addListener) preference.addListener(cancelActive);
  }
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { unload(event.target); });
  window.addEventListener('pagehide', function () {
    Array.from(pending).forEach(reveal);
    cancelActive();
    if (observer) observer.disconnect();
  });
})();
